import type { Location } from 'react-router-dom';

/**
 * Where to send someone after they log in or register.
 *
 * The return path comes from either:
 * 1. `state.from`, set by ProtectedRoute when it bounces a logged-out visitor, or
 * 2. a `?next=/path` query param, for plain links (e.g. the /apply intro page).
 *
 * Only same-site paths are honored, so `?next=https://evil.com`, `//evil.com`
 * or `/\evil.com` (browsers read `\` as `/`) falls back to the default dashboard.
 */
export const returnPath = (location: Location): string | null => {
  const from = (location.state as { from?: Location } | null)?.from;
  const candidate = from
    ? `${from.pathname}${from.search}${from.hash}`
    : new URLSearchParams(location.search).get('next');

  if (!candidate || !candidate.startsWith('/')) {
    return null;
  }
  // Resolve it the way the browser would: backslashes, tabs and newlines can
  // turn `/\evil.com` or `/\t/evil.com` into another host.
  const base = 'http://same-site.invalid';
  let url: URL;
  try {
    url = new URL(candidate, base);
  } catch {
    return null;
  }
  if (url.origin !== base) {
    return null;
  }
  const path = `${url.pathname}${url.search}${url.hash}`;
  if (/^\/(login|register)\b/.test(path)) {
    return null;
  }
  return path;
};

export const postAuthDestination = (location: Location, isAssistant: boolean): string =>
  returnPath(location) ?? (isAssistant ? '/assistant' : '/dashboard');

/** Carry the return path over to the other auth page (login <-> register). */
export const withReturnPath = (path: string, location: Location): string => {
  const next = returnPath(location);
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
};
